import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Typography, Button, Empty, Input, Switch } from 'antd'
import { SettingOutlined, SearchOutlined } from '@ant-design/icons'
import { Modal as ReactstrapModal, ModalHeader, ModalBody } from 'reactstrap'
import { checkPermission } from '../../../common/utils/helpers'
import { getTemplatedFieldOptions } from './helpers'
import { useSelector } from 'react-redux'
import { selectIsAmcLender } from '../../../store/reducers/user/userSelectors'
import { selectAllOrderData } from '../../../store/reducers/order/orderSelectors'
import { useFlags } from 'launchdarkly-react-client-sdk'
import TemplateItem from '@app/pages/adminSettings/MessageTemplateManagement/MessageTemplates/TemplateItem'

const { Text } = Typography

const MANAGE_TEMPLATES_URL = '/admin_settings?tab=messageTemplateManagement'

const useInputFocus = (visible) => {
  const inputRef = useRef(null)

  useEffect(() => {
    if (visible && inputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        inputRef.current.focus()
      }, 100)
    }
  }, [visible])

  return inputRef
}

const MessageTemplateSelectionModal = ({
  visible,
  onCancel,
  templates,
  onTemplateSelect,
  orderData,
  audienceFilter = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useInputFocus(visible)
  const isAmcLender = useSelector(selectIsAmcLender)
  const orderDataFromStore = useSelector(selectAllOrderData)
  const editMessageTemplates = checkPermission('edit_message_templates')
  const flags = useFlags() || {}
  const isUsingUnifiedConvo = flags?.conversationApproval || false
  const [showOnlyPreApproved, setShowOnlyPreApproved] = useState(isUsingUnifiedConvo)

  // Use orderData prop if available, otherwise use from store
  const currentOrderData = orderData || orderDataFromStore
  const templatedFieldOptions = getTemplatedFieldOptions(isAmcLender)

  // Filter templates based on search term, pre-approved filter, and audience filter
  const filteredTemplates = useMemo(() => {
    if (!templates || !Array.isArray(templates)) { return [] }

    let filtered = templates

    // Filter by audience
    if (audienceFilter) {
      filtered = filtered.filter(template => template.audience === audienceFilter)
    }

    // Filter by pre-approved status
    if (showOnlyPreApproved) {
      filtered = filtered.filter(template => template.is_pre_approved)
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.text.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }, [templates, searchTerm, showOnlyPreApproved, audienceFilter])

  const handleTemplateClick = (template) => {
    let templatedText = template.text
    templatedFieldOptions.forEach((option) => {
      let value = option.getValue ? option.getValue(currentOrderData) : currentOrderData[option.value]
      if (option.formatter) {
        value = option.formatter(value)
      }
      templatedText = templatedText.replace(`{{${option.id}}}`, value)
    })
    onTemplateSelect(templatedText, template)
    onCancel()
  }

  const handleCancel = () => {
    onCancel()
  }

  const handleManageTemplates = (e) => {
    // Only prevent default if it's a regular click (not middle-click or Ctrl+click)
    if (!e.ctrlKey && !e.metaKey && e.button === 0) {
      e.preventDefault()
      window.location.href = MANAGE_TEMPLATES_URL
    }
  }

  if (!visible) {
    return null
  }

  // Test with ReactstrapModal instead
  return (
    <ReactstrapModal
      isOpen={visible}
      toggle={handleCancel}
      size="lg"
    >
      <ModalHeader toggle={handleCancel}>
        Select a Message Template
      </ModalHeader>
      <ModalBody>
        {editMessageTemplates && <div style={{ marginBottom: 16 }}>
          <Button
            href={MANAGE_TEMPLATES_URL}
            icon={<SettingOutlined />}
            onClick={handleManageTemplates}
            style={{ padding: 0, height: 'auto' }}
            type="link"
          >
              Template Management Settings
          </Button>
        </div>}

        {templates.length > 0 && <div style={{ marginBottom: 16, display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Input
            ref={searchInputRef}
            placeholder="Search by template name or body..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1 }}
            allowClear
          />
          {isUsingUnifiedConvo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              <label
                htmlFor="preApprovedSwitch"
                style={{
                  fontSize: '14px',
                  color: audienceFilter === 'internal' ? '#999' : '#666',
                  cursor: audienceFilter === 'internal' ? 'not-allowed' : 'pointer',
                  lineHeight: '1',
                  margin: 0,
                }}
              >
                Show only pre-approved
              </label>
              <Switch
                id="preApprovedSwitch"
                checked={showOnlyPreApproved}
                disabled={audienceFilter === 'internal'}
                onChange={setShowOnlyPreApproved}
                size="small"
              />
            </div>
          )}
        </div>}

        <div style={{ maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', maxWidth: '768px' }}>
          {filteredTemplates && filteredTemplates.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredTemplates.map((template) => (
                <TemplateItem
                  key={template.id}
                  template={template}
                  onClick={handleTemplateClick}
                  showBadges={true}
                  isUsingUnifiedConvo={isUsingUnifiedConvo}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                      {searchTerm.trim() ? 'No templates found matching your search' : 'No templates found'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {searchTerm.trim() ? 'Try a different search term' : 'Create message templates to get started'}
                    </Text>
                  </div>
                }
              />
            </div>
          )}
        </div>
      </ModalBody>
    </ReactstrapModal>
  )
}

export default MessageTemplateSelectionModal
